import { Children, forwardRef, isValidElement, ReactNode, Ref, useEffect, useState } from 'react';
import palette from '../theme/palette';
import colors from '../theme/colors';
import MuiSelect, { selectClasses, SelectProps as MuiSelectProps } from '@mui/material/Select';
import { OverridableStringUnion } from '@mui/types';
import { InputBasePropsColorOverrides, InputBasePropsSizeOverrides } from '@mui/material/InputBase';
import { paperClasses } from '@mui/material/Paper';
import { inputClasses } from '@mui/material/Input';
import { menuItemClasses } from '@mui/material/MenuItem';
import { outlinedInputClasses } from '@mui/material';
import { styled } from '@mui/material/styles';
import { clsx } from 'clsx';
import { INPUT_BORDER_RADIUS } from '../theme/constants';
import { ChevronDown } from '@o2/icons';

export * from '@mui/material/Select';

export type SelectProps<T = unknown> = MuiSelectProps & {
  /**
   * Internal color passes internal to outlined input component and also menu component for internal border
   */
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' | 'internal',
    InputBasePropsColorOverrides
  >;
  value?: T;
  defaultValue?: T;
  size?: OverridableStringUnion<'small' | 'medium' | 'large', InputBasePropsSizeOverrides>;
};

const ForwardedSelect = forwardRef(<T,>(props: SelectProps<T>, ref: Ref<HTMLDivElement>) => {
  const { value, children, color, className, MenuProps, ...other } = props;
  const [internal, setInternal] = useState(false);

  useEffect(() => {
    Children.map(children, (child: ReactNode) => {
      if (isValidElement(child) && child.props.value === value) {
        setInternal(child.props.internal);
      }
    });
  }, [children, value]);

  return (
    <MuiSelect
      ref={ref}
      value={value}
      className={className}
      IconComponent={(props) => <ChevronDown fontSize="small" sx={{ color: colors.transparentGray[600] }} {...props} />}
      color={internal ? 'internal' : color}
      MenuProps={{
        color,
        BackdropProps: { invisible: true },
        classes: { paper: clsx(className, props.error ? `${inputClasses.error}` : '') },
        elevation: 0,
        ...MenuProps,
      }}
      {...other}
    >
      {children}
    </MuiSelect>
  );
});

const Select = styled(ForwardedSelect)(() => ({
  /**
   * Styles for opened select input.
   * Reset bottom border radius should be applied only on customized Select.
   * Border radius on MUI Select should be preserved for backward compatibility with TextField (v1) and Menu.
   */
  [`& .${selectClasses.select}[aria-expanded="true"]`]: {
    background: palette.common.white,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
  },
  [`& .${selectClasses.select}[aria-expanded="true"] ~ .${outlinedInputClasses.notchedOutline}`]: {
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomStyle: 'none',
  },
  [`&.${inputClasses.focused} .${selectClasses.select}[aria-expanded="true"] ~ .${outlinedInputClasses.notchedOutline}`]:
    {
      borderWidth: '1px',
      '&:hover': {
        borderWidth: '1px',
      },
    },
  /**
   * Styles for Select dropdown Paper, Menu, MenuItems
   */
  [`&.${paperClasses.root}`]: {
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: palette.primary.main,
    borderTopColor: colors.transparentGray[100],
    borderRadius: INPUT_BORDER_RADIUS,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    boxSizing: 'border-box',
    width: 'min-content',
  },
  [`&.Mui-error`]: {
    borderColor: palette.error.main,
    borderTopColor: colors.transparentGray[100],
  },
  [`& .${menuItemClasses.root}`]: {
    whiteSpace: 'normal',
    color: palette.text.primary,

    '&:last-of-type': {
      borderBottomLeftRadius: '4px',
      borderBottomRightRadius: '4px',
    },

    '&:first-of-type': {
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },
  [`& .${menuItemClasses.root}:hover`]: {
    backgroundColor: colors.brandPrimary[100],
    color: palette.text.primary,
  },
  [`& .${menuItemClasses.focusVisible}`]: {
    backgroundColor: palette.common.white,
    borderColor: 'transparent',
  },
  [`& .${menuItemClasses.selected}`]: {
    backgroundColor: colors.transparentGray[100],
    color: palette.text.primary,
  },
  [`& .${menuItemClasses.selected}:hover, & .${menuItemClasses.focusVisible}:hover`]: {
    backgroundColor: colors.brandPrimary[100],
  },
}));

ForwardedSelect.displayName = 'ForwardedSelect';
Select.displayName = 'Select';

export default Select as <T>(props: SelectProps<T> & { ref?: Ref<HTMLDivElement> }) => JSX.Element;
